import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { customFieldConditionsAtom } from '../../../../states/chat-list';
import { conditionsSignature, decodeConditionsFromUrl, encodeConditionsForUrl } from '../utils';
import { useFilterMetadata } from './use-filter-metadata';

const URL_PARAM = 'cfc';

export const useCustomFieldConditionsUrlSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const conditions = useAtomValue(customFieldConditionsAtom);
  const setConditions = useSetAtom(customFieldConditionsAtom);
  const { requestData: metadataRequest } = useFilterMetadata();
  const metadata = metadataRequest.data;

  const lastSyncedEncodedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!metadata) return;

    const encoded = new URLSearchParams(location.search).get(URL_PARAM) ?? '';
    if (encoded === lastSyncedEncodedRef.current) return;
    lastSyncedEncodedRef.current = encoded;

    const parsed = decodeConditionsFromUrl(encoded, metadata);
    if (conditionsSignature(parsed) === conditionsSignature(conditions)) return;

    setConditions({ type: 'set', conditions: parsed });
  }, [metadata, location.search, conditions, setConditions]);

  useEffect(() => {
    if (lastSyncedEncodedRef.current === null) return;

    const encoded = encodeConditionsForUrl(conditions);
    if (encoded === lastSyncedEncodedRef.current) return;
    lastSyncedEncodedRef.current = encoded;

    const queryParams = new URLSearchParams(location.search);
    if (encoded) {
      queryParams.set(URL_PARAM, encoded);
    } else {
      queryParams.delete(URL_PARAM);
    }

    const nextSearch = queryParams.toString();
    if (nextSearch === location.search.replace(/^\?/, '')) return;

    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [conditions, location.pathname, location.search, navigate]);
};
